import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { CalendarClock } from 'lucide-react';
import { currentDateFnsLocale, formatNumber, formatPercent } from '@/i18n/locale';
import type { HeatmapCell } from '@/api/endpoints/analytics';
import { LoadError } from './LoadError';

/**
 * Connect rate by day of the week and hour (W4, audit B2-analytics, Minari
 * « Connect rate » heatmap). Counted by the server over every call of the
 * period, in the browser's time zone (POST /api/analytics/dialer/heatmap).
 *
 * One hue, darker = higher connect rate; a cell with fewer than three calls
 * is drawn as « too few calls » so one lucky call does not look like the best
 * slot. Hours 8 h to 21 h are always shown, widened to any hour with calls.
 */

const MIN_CALLS = 3;
// 2024-01-01 is a Monday: day d (1 = Monday) is 2024-01-0d.
const dayDate = (d: number) => new Date(2024, 0, d);

export default function DayHourHeatmap({ cells, state, onRetry, error }: { cells: HeatmapCell[]; state: 'loading' | 'error' | 'ready'; onRetry: () => void; error?: unknown }) {
    const { t } = useTranslation(['dialer', 'common']);
    const locale = currentDateFnsLocale();

    const { hours, byKey, maxRate, best } = useMemo(() => {
        const withCalls = cells.filter((c) => c.total > 0);
        const lo = Math.min(8, ...withCalls.map((c) => c.hour));
        const hi = Math.max(21, ...withCalls.map((c) => c.hour));
        const map = new Map(cells.map((c) => [`${c.day}-${c.hour}`, c]));
        const rated = withCalls.filter((c) => c.total >= MIN_CALLS);
        const top = rated.reduce<HeatmapCell | null>((acc, c) => (!acc || c.connected / c.total > acc.connected / acc.total ? c : acc), null);
        return {
            hours: Array.from({ length: hi - lo + 1 }, (_, i) => lo + i),
            byKey: map,
            maxRate: Math.max(0.0001, ...rated.map((c) => c.connected / c.total)),
            best: top,
        };
    }, [cells]);

    const dayName = (d: number, style: 'EEE' | 'EEEE') => format(dayDate(d), style, { locale });

    return (
        <section className="bg-card rounded-xl border border-border overflow-hidden min-w-0" data-testid="analytics-day-hour-heatmap">
            <div className="px-5 py-4 border-b border-border/50 flex items-start gap-2 min-w-0">
                <CalendarClock className="w-5 h-5 shrink-0 mt-0.5" style={{ color: 'var(--vf-text-3)' }} aria-hidden />
                <div className="min-w-0">
                    <h2 className="text-sm font-semibold text-foreground">
                        {best
                            ? t('dialer:analytics.heatmap.finding', { day: dayName(best.day, 'EEEE'), hour: t('dialer:analytics.hourLabel', { h: best.hour }), rate: formatPercent((best.connected / best.total) * 100) })
                            : t('dialer:analytics.heatmap.title')}
                    </h2>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--vf-text-3)' }}>{t('dialer:analytics.heatmap.subtitle', { min: MIN_CALLS })}</p>
                </div>
            </div>
            <div className="p-5">
                {state === 'loading' && cells.length === 0 ? (
                    <div className="h-48 animate-pulse motion-reduce:animate-none rounded bg-[var(--vf-surface-3)]" />
                ) : state === 'error' ? (
                    <LoadError error={error} onRetry={onRetry} />
                ) : cells.length === 0 ? (
                    <p className="text-sm text-center py-10 text-muted-foreground">{t('dialer:analytics.noCallsPeriod')}</p>
                ) : (
                    <div className="overflow-x-auto vf-scroll-visible">
                        <table className="border-separate" style={{ borderSpacing: 3 }}>
                            <caption className="sr-only">{t('dialer:analytics.heatmap.title')}</caption>
                            <thead>
                                <tr>
                                    <td />
                                    {hours.map((h) => (
                                        <th key={h} scope="col" className="text-[10px] font-normal tabular-nums text-muted-foreground px-0.5">{t('dialer:analytics.hourLabel', { h })}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {[1, 2, 3, 4, 5, 6, 7].map((d) => (
                                    <tr key={d}>
                                        <th scope="row" className="text-xs font-normal text-muted-foreground pr-2 text-left whitespace-nowrap capitalize">{dayName(d, 'EEE')}</th>
                                        {hours.map((h) => {
                                            const c = byKey.get(`${d}-${h}`);
                                            const total = c?.total ?? 0;
                                            const rate = total > 0 ? c!.connected / total : 0;
                                            const enough = total >= MIN_CALLS;
                                            const label = total === 0
                                                ? t('dialer:analytics.heatmap.cellEmpty', { day: dayName(d, 'EEEE'), hour: t('dialer:analytics.hourLabel', { h }) })
                                                : t('dialer:analytics.heatmap.cell', { day: dayName(d, 'EEEE'), hour: t('dialer:analytics.hourLabel', { h }), calls: formatNumber(total), connected: formatNumber(c!.connected), rate: formatPercent(rate * 100) });
                                            return (
                                                <td
                                                    key={h}
                                                    title={label}
                                                    aria-label={label}
                                                    className="w-8 h-7 rounded text-center text-[10px] tabular-nums"
                                                    style={{
                                                        background: total === 0
                                                            ? 'var(--vf-surface-2)'
                                                            : enough
                                                                ? `color-mix(in srgb, var(--vf-accent) ${Math.round((0.18 + 0.82 * (rate / maxRate)) * 100)}%, var(--vf-surface-2))`
                                                                : 'var(--vf-surface-3)',
                                                        color: enough && rate / maxRate > 0.55 ? '#fff' : 'var(--vf-text-2)',
                                                    }}
                                                >
                                                    {total > 0 ? formatNumber(total) : ''}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <p className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-3 text-[11px]" style={{ color: 'var(--vf-text-3)' }}>
                            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: 'var(--vf-accent)', opacity: 0.25 }} />{t('dialer:analytics.heatmap.legendLow')}</span>
                            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: 'var(--vf-accent)' }} />{t('dialer:analytics.heatmap.legendHigh')}</span>
                            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: 'var(--vf-surface-3)' }} />{t('dialer:analytics.heatmap.legendFew', { min: MIN_CALLS })}</span>
                            <span>{t('dialer:analytics.heatmap.legendCount')}</span>
                        </p>
                    </div>
                )}
            </div>
        </section>
    );
}
